import { Form } from 'antd';
import { useMemo } from 'react';

import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';

import type { RowData } from '../types';
import { GUEST_TYPES, PRICE_TYPES } from '../types';

export const usePriceDataSource = (
  hasDayPrice: boolean,
  hasAdultChildPrice: boolean
): RowData[] => {
  const form = Form.useFormInstance();
  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );
  const allowedPax = Form.useWatch(['allowedPaxTypes'], form);

  return useMemo(() => {
    const filteredPriceTypes = isAlwaysIncludedWithNight
      ? PRICE_TYPES.filter((pf) => pf !== 'Night stay')
      : PRICE_TYPES;

    const hasAdultOnly =
      allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.ADULT;
    const hasChildOnly =
      allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.CHILD;

    const filteredGuestTypes = hasAdultOnly
      ? GUEST_TYPES.filter((g) => g === 'Adult')
      : hasChildOnly
      ? GUEST_TYPES.filter((g) => g === 'Child')
      : GUEST_TYPES;

    if (!hasDayPrice && !hasAdultChildPrice) {
      return [{ key: 'price-row' }];
    }

    if (hasDayPrice && !hasAdultChildPrice) {
      return filteredPriceTypes.map((type) => ({
        key: `${type.toLowerCase().replace(/\s+/g, '-')}-row`,
        type,
      }));
    }

    if (!hasDayPrice && hasAdultChildPrice) {
      return filteredGuestTypes.map((guest) => ({
        key: `${guest.toLowerCase()}-row`,
        guest,
      }));
    }

    return filteredPriceTypes.flatMap((type) =>
      filteredGuestTypes.map((guest) => ({
        key: `${type
          .toLowerCase()
          .replace(/\s+/g, '-')}-${guest.toLowerCase()}`,
        type,
        guest,
      }))
    );
  }, [hasDayPrice, hasAdultChildPrice, isAlwaysIncludedWithNight, allowedPax]);
};
